import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  DashboardCard,
  FilterBar,
  PageHeader,
  StatusBadge,
  cn,
} from '@zafroi/ui-kit';
import { BookOpen, Inbox, MessageSquare } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useConversations } from '@/hooks/useEngageQueries';
import { useNewSinceAnchor } from '@/hooks/useNewSinceAnchor';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { NewSincePill } from '@/components/common/NewSincePill';
import {
  channelKindLabel,
  conversationStatusVariants,
} from '@/types/status-variants';
import { relativeTime } from '@/lib/format';
import type { Conversation, ConversationChannel } from '@/types/engage';

const CHANNEL_FILTERS: Array<{
  id: ConversationChannel | 'all';
  label: string;
}> = [
  { id: 'all', label: 'All channels' },
  { id: 'email', label: 'Email' },
  { id: 'portal', label: 'Portal' },
  // WhatsApp / Phone / SMS filters are hidden until the adapters ship —
  // see docs/BACKLOG.md "Channels epic". Re-enable them when those
  // channels can actually produce conversations.
];

export function ConversationInboxPage() {
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId?: string }>();
  // 30s polling scoped to the page only — Dashboard/sidebar callers of the
  // same hook keep their default behaviour (no background poll).
  const query = useConversations({ refetchInterval: 30_000 });
  const [search, setSearch] = useState('');
  const [channel, setChannel] = useState<ConversationChannel | 'all'>('all');

  const conversations = query.data ?? [];
  // Conversations sort by `lastMessageAt`, so that's the field that says
  // "something new landed in this thread" — including new inbound replies
  // on existing threads, not just brand-new conversations.
  const { newCount, isNew, acknowledge } = useNewSinceAnchor<Conversation>(
    conversations,
    (conv) => conv.lastMessageAt,
  );
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return conversations.filter((conv) => {
      if (channel !== 'all' && conv.channel !== channel) return false;
      if (!needle) return true;
      return (
        conv.customerName.toLowerCase().includes(needle) ||
        conv.subject?.toLowerCase().includes(needle) ||
        conv.preview.toLowerCase().includes(needle)
      );
    });
  }, [conversations, search, channel]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Live engagement"
        title="Conversations"
        description="Every chat with every customer, across all channels — one timeline per customer, never lost. Customers reach you here; you triage and respond."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/conversations/guide">
              <BookOpen className="h-3.5 w-3.5" />
              Help &amp; Guide
            </Link>
          </Button>
        }
      />

      <DashboardCard flushBody icon={Inbox} title="Inbox">
        <NewSincePill
          count={newCount}
          onAcknowledge={acknowledge}
          noun={{ singular: 'conversation', plural: 'conversations' }}
        />
        <div className="border-b border-border px-5 py-3">
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by customer, subject, or message…"
            filters={CHANNEL_FILTERS.map((opt) => (
              <Button
                key={opt.id}
                variant={channel === opt.id ? 'soft' : 'outline'}
                size="sm"
                onClick={() => setChannel(opt.id)}
              >
                {opt.label}
              </Button>
            ))}
            hasActiveFilters={channel !== 'all' || search.length > 0}
            onClear={() => {
              setChannel('all');
              setSearch('');
            }}
          />
        </div>

        <QueryStateGuard
          data={filtered}
          isLoading={query.isLoading}
          isError={query.isError}
          emptyTitle="No conversations match this view"
          emptyDescription="Try a different channel or adjust your search."
        >
          {(rows) => (
            <ul className="divide-y divide-border">
              {rows.map((conv) => {
                const active = conv.id === conversationId;
                const fresh = isNew(conv);
                return (
                  <li key={conv.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/conversations/${conv.id}`)}
                      className={cn(
                        'flex w-full items-start gap-4 border-l-4 border-l-transparent px-5 py-4 text-left transition-colors hover:bg-muted/50 focus:bg-muted/50 focus:outline-none',
                        active && 'bg-muted/60',
                        fresh && 'border-l-info bg-info/5',
                      )}
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <MessageSquare className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-semibold text-foreground">
                            {conv.customerName}
                          </span>
                          {conv.unread && (
                            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                          )}
                          <Badge tone="neutral" withDot={false}>
                            {channelKindLabel[conv.channel] ?? conv.channel}
                          </Badge>
                        </div>
                        {conv.subject && (
                          <div className="mt-0.5 truncate text-xs font-medium text-foreground">
                            {conv.subject}
                          </div>
                        )}
                        <div className="mt-0.5 truncate text-xs text-muted-foreground">
                          {conv.preview}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1.5">
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground tnum">
                          {relativeTime(conv.lastMessageAt)}
                        </span>
                        <StatusBadge
                          status={conv.status}
                          variants={conversationStatusVariants}
                        />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </QueryStateGuard>
      </DashboardCard>
    </div>
  );
}
